/* eslint-disable react/forbid-dom-props */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Download, SendHorizonal } from 'lucide-react';
import toast from 'react-hot-toast';
import { petitionsApi } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import { PERMISSIONS } from '../rbac/permissions';
import { triggerBlobDownload } from '../utils/download';

/**
 * A peça que o controle de qualidade reprovou, com o motivo e o que fazer com ela.
 *
 * Reprovar nunca destruiu a peça: o `.docx` fica salvo e a rota de download devolve 200. O que
 * acontecia é que o caso voltava para `ficha_preenchimento` e a tela só mostrava um toast — o
 * documento existia e ninguém alcançava. Quem gerou via "revise a ficha", e quem poderia julgar o
 * defeito nunca recebia o caso. O gate deixava de proteger e passava a esconder.
 *
 * Aqui o defeito fica escrito na tela e a decisão volta a ser de gente: baixar e conferir, corrigir
 * a ficha (é o que o painel de perguntas ao lado resolve), ou assumir a pendência e mandar para
 * revisão. Assumir exige `petition:review` e vai para a auditoria com nome e rótulo do check.
 */
export default function PeticaoReprovadaPanel({ caseId, clientName }) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const podeRevisar = user?.permissions?.includes(PERMISSIONS.PETITION_REVIEW);
  const podeBaixar = podeRevisar || user?.permissions?.includes(PERMISSIONS.PETITION_APPROVE);

  const { data: peticao } = useQuery({
    queryKey: ['peticao-do-caso', caseId],
    queryFn: () => petitionsApi.getByCaseId(caseId),
    enabled: !!caseId,
  });

  // O motivo de assumir vai para o histórico do caso — o conector já o exige; aqui é opcional, para
  // não travar quem só quer mandar a peça adiante (relatório do Dr. Lucas de 13/10/2026).
  const [motivo, setMotivo] = useState('');
  // A API pede ao menos 15 caracteres quando há motivo: "falso positivo" (14) voltava 422 sem dizer por quê.
  const motivoCurto = motivo.trim().length > 0 && motivo.trim().length < 15;
  const enviar = useMutation({
    mutationFn: () => petitionsApi.enviarParaRevisao(peticao.id, motivo.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peticao-do-caso', caseId] });
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Enviado para revisão com a pendência registrada em auditoria.');
    },
    onError: (erro) => toast.error(
      erro?.response?.data?.detail || 'Não foi possível enviar para revisão.',
    ),
  });

  if (!peticao || peticao.status !== 'gerada_com_pendencias') return null;

  const criticos = (peticao.quality_report?.checks || []).filter(
    c => c.status === 'failed' && c.severity === 'critical',
  );

  async function baixar() {
    try {
      const blob = await petitionsApi.download(peticao.id, 'docx');
      const cliente = (clientName || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      triggerBlobDownload(blob, `peticao_v${peticao.version}_${cliente}.docx`);
    } catch {
      toast.error('Erro ao baixar a petição.');
    }
  }

  return (
    <div className="card" style={{ borderLeft: '3px solid var(--error)' }}>
      <div className="card-header">
        <AlertOctagon size={15} style={{ color: 'var(--error)' }} />
        <span className="card-title">Petição v{peticao.version} reprovada no controle de qualidade</span>
        <span className="badge">{criticos.length}</span>
      </div>
      <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', margin: '0 0 .75rem' }}>
        A peça foi gerada e está salva. O caso não avançou para revisão por causa do que está abaixo.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        {criticos.map((c, i) => (
          <div key={c.id || i} style={{ borderTop: '1px solid var(--border-light)', paddingTop: '.5rem' }}>
            <div style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>{c.label}</div>
            <div style={{ fontSize: '.73rem', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
              {c.message}
            </div>
          </div>
        ))}
      </div>

      {podeRevisar && (
        <label style={{ display: 'block', fontSize: '.73rem', color: 'var(--text-secondary)', marginTop: '.875rem' }}>
          Por que a pendência pode ser assumida? <span className="opt">(opcional, vai para o histórico)</span>
          <textarea
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={2}
            placeholder="Ex.: o crítico de percurso leu o horário de embarque como duração; a seção confere com a ficha."
            style={{ width: '100%', marginTop: '.25rem', resize: 'vertical' }}
          />
          {motivoCurto && <span className="opt">Escreva pelo menos 15 caracteres, ou deixe em branco.</span>}
        </label>
      )}

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '.875rem' }}>
        {podeBaixar && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={baixar}>
            <Download size={13} /> Baixar e conferir
          </button>
        )}
        {podeRevisar && (
          <button
            type="button"
            className="btn btn-sm"
            style={{ border: '1px solid var(--border)' }}
            disabled={enviar.isPending || motivoCurto}
            onClick={() => enviar.mutate()}
          >
            <SendHorizonal size={13} />
            {enviar.isPending ? 'Enviando…' : 'Assumir a pendência e enviar para revisão'}
          </button>
        )}
      </div>
    </div>
  );
}
